/* ============================================================
   dd-welcome-sticky.js

   Owns the visibility lifecycle + interactions for the homepage
   welcome-offer floating sticky. See sections/dd-welcome-sticky.liquid
   for the data contract and assets/dd-welcome-sticky.css for the
   visual states.

   Responsibilities:
     1. Resolve the active offer:
          a. Server-rendered metafield  (logged-in customer with
             `customer.metafields.custom.welcome_offer_*`).
          b. localStorage seed  (`dd_welcome_offer`, written by
             the announcement bar's postMessage listener after a
             successful apply-form submission).
        If neither path yields a fresh code, the sticky stays
        `hidden` and we exit early — no scroll listener attached.
     2. Honor the `sessionStorage` dismiss flag set by the × button.
        The flag stores the dismissed code's literal value, so a
        new (different) code re-shows after a previous dismissal.
     3. Show/hide on scroll. Threshold is `data-dd-threshold-vh%`
        of viewport height (read from the section schema). Uses
        a passive scroll listener + a single rAF coalesce.
     4. Tick the HRS:MIN:SEC countdown every second; auto-hide
        once the offer's `endsAt` passes. The interval is paused
        when the tab is hidden to avoid background battery drain
        and resumes on `visibilitychange`.
     5. Copy code to clipboard via the copy button. Falls back
        to the legacy `document.execCommand('copy')` path on
        browsers without `navigator.clipboard`. Success state
        cross-fades the icon to a checkmark and reveals an
        aria-live confirmation.
     6. Add to cart on CTA click. Uses `window.DDCartApi.add()`
        (loaded from assets/dd-cart-api.js — already used by
        dd-card2.js + dd-sticky-footer.js so the cart-drawer
        sections=cart-drawer,cart-icon-bubble re-render path is
        consistent across surfaces). After a successful add, the
        cart drawer opens automatically so the customer's next
        action ("paste my code") is one beat away.

   Why a single self-contained module:
     The announcement bar already owns the cross-surface seed
     pipeline (postMessage → localStorage). We deliberately don't
     duplicate that here — we only READ the localStorage entry it
     produced. That keeps the contract one-way and avoids a race
     between two listeners writing to the same key.
   ============================================================ */

(function () {
  'use strict';

  var STORAGE_KEY = 'dd_welcome_offer';
  var DISMISS_KEY = 'dd_welcome_sticky_dismissed';
  var COUNTDOWN_TICK_MS = 1000;
  var COPY_SUCCESS_DURATION_MS = 1800;
  var ADDED_RESET_MS = 1600;
  /* Hard upper bound for `endsAt` validation. Any stored or server-issued
     offer claiming a window longer than this is treated as malformed and
     dropped — neutralises far-future writes (year-2099 typos, automation
     bugs) that would otherwise live forever in localStorage. Mirrors the
     identical cap in announcement-bar.liquid so writer + reader never
     disagree. */
  var MAX_OFFER_WINDOW_MS = 90 * 24 * 60 * 60 * 1000;

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn, { once: true });
    } else {
      fn();
    }
  }

  /* Opt-in diagnostic. Append `?ddws=debug` to any storefront URL
     (or set `window.__ddWelcomeDebug = true` before this script
     runs) to log every guard the mount controller passes through.
     Stays a no-op in production builds — gated by a fresh URL
     param read each load. */
  var DEBUG = (function () {
    if (window.__ddWelcomeDebug === true) return true;
    try {
      var p = new URLSearchParams(window.location.search);
      return p.get('ddws') === 'debug';
    } catch (e) { return false; }
  })();

  function dbg() {
    if (!DEBUG) return;
    var args = Array.prototype.slice.call(arguments);
    args.unshift('[dd-welcome-sticky]');
    /* eslint-disable-next-line no-console */
    (window.console && console.log).apply(console, args);
  }

  ready(function () {
    var stickies = document.querySelectorAll('[data-dd-welcome-sticky]');
    dbg('ready', { found: stickies.length });
    /* Multi-instance friendly. The schema gates `enabled_on:
       templates: [index]` and merchants typically place exactly
       one — but if duplicate sections ever land in the same JSON,
       each one gets its own controller. */
    for (var i = 0; i < stickies.length; i++) {
      mountController(stickies[i]);
    }

    /* Theme editor: Shopify reloads a section's HTML by replacing the
       entire `#shopify-section-{id}` wrapper. Because we portal the
       `<aside>` out to <body> at mount, the *previous* aside survives
       that swap and would coexist with the freshly-rendered one. The
       `shopify:section:load` event fires after the swap with the new
       section's id; we use it to find any prior portaled asides keyed
       to that section id and remove them before re-mounting the new
       copy. Production builds never see this event so it's a no-op
       on the live storefront. */
    /* Selectors below intentionally drop the `body >` prefix the
       earlier portal-to-body implementation used. Portaled copies
       now live inside `.dd-nudge-stack` (or, in degraded contexts,
       the body-level fallback wrapper getOrCreateStack creates), so
       a parent-agnostic match catches them in either location. */
    document.addEventListener('shopify:section:load', function (e) {
      var sectionId = e && e.detail && e.detail.sectionId;
      if (!sectionId) return;
      var fresh = document.querySelector(
        '#shopify-section-' + sectionId + ' [data-dd-welcome-sticky]'
      );
      if (!fresh) return;
      var prior = document.querySelectorAll(
        '[data-dd-welcome-sticky][data-dd-section-id="' + sectionId + '"][data-dd-portaled="true"]'
      );
      for (var i = 0; i < prior.length; i++) prior[i].parentNode.removeChild(prior[i]);
      mountController(fresh);
    });

    document.addEventListener('shopify:section:unload', function (e) {
      var sectionId = e && e.detail && e.detail.sectionId;
      if (!sectionId) return;
      var prior = document.querySelectorAll(
        '[data-dd-welcome-sticky][data-dd-section-id="' + sectionId + '"][data-dd-portaled="true"]'
      );
      for (var i = 0; i < prior.length; i++) prior[i].parentNode.removeChild(prior[i]);
    });
  });

  function mountController(sticky) {
    var offer = resolveOffer(sticky);
    if (!offer) {
      dbg('mount.bail', {
        reason: 'no-offer',
        serverCode: sticky.getAttribute('data-dd-welcome-code') || '',
        serverEndsAt: sticky.getAttribute('data-dd-ends-at') || '',
        localStorage: safeReadLocalStorage()
      });
      return;
    }

    if (isDismissed(offer.code)) {
      dbg('mount.bail', { reason: 'dismissed', code: offer.code });
      return;
    }

    dbg('mount.ok', { code: offer.code, endsAt: offer.endsAt });

    /* Portal the sticky into the .dd-nudge-stack wrapper.

       The section is registered in templates/index.json so Shopify
       renders it inside `<main id="MainContent">`. dd-footer.css gives
       `#MainContent` `position: relative; z-index: 1` to drive the
       parallax footer reveal. That establishes a stacking context, and
       any descendant — even a `position: fixed` one — has its z-index
       evaluated *inside* that context, so any sibling of `#MainContent`
       (cart drawer, chat widget, etc.) paints over us regardless of
       how high we crank the value.

       The .dd-nudge-stack wrapper lives at body-level (rendered in
       layout/theme.liquid outside #MainContent) and owns positioning
       for every bottom-anchored nudge bar — review, education, and
       welcome. Portaling here gives this aside the wrapper's z-index,
       safe-area offset, and flex-column layout, so when both the
       welcome sticky AND a nudge bar are eligible they stack with a
       10px gap instead of overlaying.

       The wrapper is rendered unconditionally in layout/theme.liquid
       (universal — review/education snippets self-gate inside `{% if
       customer %}`). If for any reason the wrapper isn't present at
       portal time (theme editor preview of an isolated section, JSON
       save mid-render, etc.) we materialize one as a body child so
       the welcome sticky still has a home — visible behavior is the
       same as a stack with one child.

       We move once, on first mount; section re-renders in the theme
       editor produce a fresh `[data-dd-welcome-sticky]` and
       mountController runs again on it, portaling the new copy and
       orphaning the previous one for GC. */
    var stack = getOrCreateStack();
    if (sticky.parentNode !== stack) {
      sticky.setAttribute('data-dd-portaled', 'true');
      stack.appendChild(sticky);
      dbg('mount.portaled', { parent: '.dd-nudge-stack' });
    }

    /* Clear any stale `.is-footer-visible` class. The footer-hide
       feature is currently disabled (see comment below), but this
       guards against a previously-cached JS version having added
       the class while the new asset propagates. */
    sticky.classList.remove('is-footer-visible');

    hydrate(sticky, offer);
    wireCopy(sticky, offer);
    wireAddToCart(sticky);
    wireInCartSync(sticky);
    wireDismiss(sticky, offer);
    wireScroll(sticky);
    /* DIAGNOSTIC: footer-in-view hide temporarily disabled. The
       page footer is `position: fixed; z-index: 0` (parallax-reveal
       pattern) and our scroll-based check on `#MainContent` was
       still flipping `.is-footer-visible` on at the wrong moment,
       leaving the sticky permanently invisible despite `.is-shown`
       being correctly set. Re-enable after verifying that scroll
       threshold + dismiss are the only gates the user sees. */
    // wireFooterHide(sticky);
    wireCountdown(sticky, offer);
  }

  function safeReadLocalStorage() {
    try { return localStorage.getItem(STORAGE_KEY); }
    catch (e) { return '<<read failed: ' + e.message + '>>'; }
  }

  /* Resolve the shared `.dd-nudge-stack` wrapper that hosts every
     bottom-anchored nudge bar. layout/theme.liquid renders this
     element unconditionally for both guests and customers, so the
     querySelector should hit on the very first call in the wild —
     the create-and-append fallback only fires in degraded contexts
     (theme editor preview of an orphaned section, JSON save mid-
     render, future code paths that render this aside outside the
     standard layout). When we create it ourselves, we mirror the
     production class name so dd-nudge-stack.css applies and the new
     element drops in seamlessly if/when the canonical layout copy
     arrives later. */
  function getOrCreateStack() {
    var stack = document.querySelector('.dd-nudge-stack');
    if (stack) return stack;
    stack = document.createElement('div');
    stack.className = 'dd-nudge-stack';
    document.body.appendChild(stack);
    return stack;
  }

  /* Resolve the visitor's identity. Liquid (sections/dd-welcome-sticky.liquid
     + sections/announcement-bar.liquid) injects `window.__ddCustomerId` on
     every render — numeric for logged-in customers, `null` for guests. Cast
     to string for stable string-string comparisons against the (also string-
     normalised) `customerId` field on stored seeds.

     Falls back to `window.Shopify.customer.id` for the rare case where the
     Liquid injection didn't run (cached HTML pre-rollout, edge cases). When
     both are missing, treat as guest. */
  function getCurrentCustomerId() {
    var injected = window.__ddCustomerId;
    if (injected !== undefined && injected !== null) return String(injected);
    if (injected === null) return null;
    try {
      var sc = window.Shopify && window.Shopify.customer;
      if (sc && sc.id) return String(sc.id);
    } catch (_e) {}
    return null;
  }

  /* ------------------------------------------------------------------
     Offer resolution — server-rendered first, localStorage fallback.
     The localStorage path is identity-bound: a seed is only honored if
     its `customerId` field matches the current visitor (or both are
     `null` for guest-on-guest). Any mismatch (account switch, shared
     browser, legacy pre-customerId seed) purges the seed.
     ------------------------------------------------------------------ */

  function resolveOffer(sticky) {
    var serverCode = sticky.getAttribute('data-dd-welcome-code') || '';
    var serverEndsAt = sticky.getAttribute('data-dd-ends-at') || '';
    if (serverCode && isFresh(serverEndsAt)) {
      return { code: serverCode, endsAt: serverEndsAt };
    }

    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;

      var parsed;
      try { parsed = JSON.parse(raw); }
      catch (_e) { localStorage.removeItem(STORAGE_KEY); return null; }

      if (!parsed || !parsed.code || !isFresh(parsed.endsAt)) {
        /* Expired / malformed — purge so neither this module nor the
           announcement-bar marquee retries with bad data on the next page. */
        localStorage.removeItem(STORAGE_KEY);
        return null;
      }

      /* Identity gate. The seed must explicitly carry a `customerId`
         field (added in the rollout that hardened the visibility model);
         seeds without one are treated as legacy and purged so the next
         postMessage can re-issue with proper binding. The match check
         covers both directions: customer-A seed → customer-B browser
         (mismatch, purge) and guest seed → logged-in browser (mismatch,
         purge). Guest-on-guest is the only legitimate cross-session
         survival path. */
      var seedCid = (typeof parsed.customerId === 'undefined')
        ? '<<legacy>>'
        : (parsed.customerId === null ? null : String(parsed.customerId));
      var currentCid = getCurrentCustomerId();
      if (seedCid !== currentCid) {
        dbg('seed.purge', { reason: 'identity-mismatch', seedCid: seedCid, currentCid: currentCid });
        localStorage.removeItem(STORAGE_KEY);
        return null;
      }

      return { code: String(parsed.code), endsAt: String(parsed.endsAt) };
    } catch (e) { /* private mode / disabled storage */ }

    return null;
  }

  function isFresh(endsAt) {
    if (!endsAt) return false;
    var t = new Date(endsAt).getTime();
    if (!isFinite(t) || t <= Date.now()) return false;
    /* 90-day upper bound. See MAX_OFFER_WINDOW_MS comment at module top. */
    if (t - Date.now() > MAX_OFFER_WINDOW_MS) return false;
    return true;
  }

  /* ------------------------------------------------------------------
     Hydration — fill DOM with the resolved offer (no-op when the
     server already filled it in for logged-in customers).
     ------------------------------------------------------------------ */

  function hydrate(sticky, offer) {
    sticky.setAttribute('data-dd-welcome-code', offer.code);
    sticky.setAttribute('data-dd-ends-at', offer.endsAt);

    /* Both desktop and mobile variants render their own code chip
       with the same data-attr — write to both so a viewport resize
       across the 640px breakpoint never reveals a stale code. */
    var codeEls = sticky.querySelectorAll('[data-dd-welcome-code-text]');
    for (var i = 0; i < codeEls.length; i++) codeEls[i].textContent = offer.code;

    /* Initial countdown render so the very first paint after
       reveal isn't a frame of "00 : 00 : 00" placeholder. */
    renderCountdown(sticky, offer.endsAt);
  }

  /* ------------------------------------------------------------------
     Dismiss — value-aware (keyed to the literal code) so a fresh
     code re-shows after a previous dismissal of an older code.
     ------------------------------------------------------------------ */

  function isDismissed(code) {
    try { return sessionStorage.getItem(DISMISS_KEY) === code; }
    catch (e) { return false; }
  }

  function rememberDismissed(code) {
    try { sessionStorage.setItem(DISMISS_KEY, code); } catch (e) {}
  }

  function wireDismiss(sticky, offer) {
    /* Desktop + mobile each render their own × button. Wire both so
       whichever copy is visible at click-time fires; the slideOut
       hides the entire `<aside>` so neither copy needs to know
       about the other's lifecycle. */
    var btns = sticky.querySelectorAll('[data-dd-welcome-dismiss]');
    for (var i = 0; i < btns.length; i++) {
      btns[i].addEventListener('click', function () {
        rememberDismissed(offer.code);
        slideOut(sticky);
      });
    }
  }

  function slideOut(sticky) {
    sticky.classList.remove('is-shown');
    sticky.setAttribute('aria-hidden', 'true');
    /* No [hidden] attribute toggle. The CSS default
       (.dd-welcome-sticky { display: none }) reasserts itself the
       moment .is-shown is removed, so the bar drops out of layout
       without us managing a parallel attribute. */
  }

  /* ------------------------------------------------------------------
     Copy-to-clipboard — modern API with execCommand fallback.
     ------------------------------------------------------------------ */

  function wireCopy(sticky, offer) {
    /* Both variants get their own copy button. Wrap the iteration
       in an IIFE so each handler closes over its own `btn`
       reference — without it, the loop var leaks and only the
       last button gets the success state on copy. */
    var btns = sticky.querySelectorAll('[data-dd-welcome-copy]');
    for (var i = 0; i < btns.length; i++) {
      (function (btn) {
        btn.addEventListener('click', function () {
          copyText(offer.code).then(function (ok) {
            if (!ok) return;
            showCopySuccess(sticky, btn);
          });
        });
      })(btns[i]);
    }
  }

  function copyText(text) {
    /* `navigator.clipboard.writeText` is the modern path; it
       requires a secure context (HTTPS) which storefront pages
       always provide. We still fall back to execCommand for the
       rare in-app browser that doesn't expose Clipboard API
       even on HTTPS (older WebViews on Android). */
    if (navigator.clipboard && window.isSecureContext) {
      return navigator.clipboard.writeText(text)
        .then(function () { return true; })
        .catch(function () { return execCommandCopyFallback(text); });
    }
    return Promise.resolve(execCommandCopyFallback(text));
  }

  function execCommandCopyFallback(text) {
    try {
      var ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      ta.style.pointerEvents = 'none';
      ta.setAttribute('readonly', '');
      document.body.appendChild(ta);
      ta.select();
      var ok = document.execCommand('copy');
      document.body.removeChild(ta);
      return !!ok;
    } catch (e) {
      return false;
    }
  }

  function showCopySuccess(sticky, btn) {
    btn.classList.add('is-copied');
    sticky.classList.add('is-copy-success');
    /* Reset after the success window so the user can copy again
       (e.g. after switching to the cart drawer and back). */
    window.clearTimeout(btn._ddResetTimer);
    btn._ddResetTimer = window.setTimeout(function () {
      btn.classList.remove('is-copied');
      sticky.classList.remove('is-copy-success');
    }, COPY_SUCCESS_DURATION_MS);
  }

  /* ------------------------------------------------------------------
     Add to cart — DDCartApi.add → cart-drawer renderContents.
     ------------------------------------------------------------------ */

  function wireAddToCart(sticky) {
    /* Two CTAs to wire when both desktop + mobile variants are
       rendered. Each gets its own click handler scoped to its own
       label/spinner DOM, but they share the same labels object and
       variant id so post-add state lookups stay consistent. */
    var ctas = sticky.querySelectorAll('[data-dd-welcome-cta]');
    if (!ctas.length) return;

    var variantId = sticky.getAttribute('data-dd-variant-id');
    if (!variantId) {
      /* No variant resolved server-side (out of stock or product
         misconfigured). Disable click-through on every variant and
         let the disabled attribute set in Liquid carry the visual
         state. */
      for (var k = 0; k < ctas.length; k++) ctas[k].setAttribute('disabled', '');
      return;
    }

    var labels = {
      def: sticky.getAttribute('data-dd-cta-default') || 'Add to cart',
      loading: sticky.getAttribute('data-dd-cta-loading') || 'Adding…',
      added: sticky.getAttribute('data-dd-cta-added') || 'Added',
      inCart: sticky.getAttribute('data-dd-cta-in-cart') || 'Added'
    };

    for (var i = 0; i < ctas.length; i++) {
      (function (cta) {
        var labelEl = cta.querySelector('[data-dd-welcome-cta-label]');
        /* Stash labels on the element so the cart-sync listener can
           re-derive them without re-reading dataset attrs on every
           cart:updated event. */
        cta._ddLabels = labels;

        cta.addEventListener('click', function (e) {
          e.preventDefault();
          if (cta.classList.contains('is-loading')) return;
          /* In-cart state is the dominant path — clicking re-opens
             the cart drawer instead of stacking another unit. The
             server-side `cart.items` loop in dd-welcome-sticky.liquid
             primes `.is-in-cart` on first paint; the wireInCartSync
             listener below keeps it accurate across mid-session
             add/remove events from any other surface. */
          if (cta.classList.contains('is-in-cart')) {
            openCartDrawer(cta);
            return;
          }
          addToCart(sticky, cta, labelEl, labels, variantId);
        });
      })(ctas[i]);
    }
  }

  function openCartDrawer(trigger) {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    try {
      if (typeof drawer.open === 'function') {
        drawer.open(trigger || undefined);
        return;
      }
    } catch (_e) {}
    /* Fallback for cart-drawer custom elements that expose only
       a class-toggle API (older Dawn forks): mirror the open
       semantics by adding `active` to the drawer + `open` body
       lock, the two classes Dawn checks for the open state. */
    try {
      drawer.classList.add('active');
      document.body.classList.add('overflow-hidden');
    } catch (_e) {}
  }

  /* Apply a Shopify discount code to the active cart by fetching the
     canonical `/discount/{code}` route. Shopify responds with a 302
     to `/` and sets a `discount_code` cookie that the cart engine
     reads on every subsequent cart mutation — so any `/cart/add.js`
     fired AFTER this resolves returns section HTML with the discount
     already factored into line items + totals.

     Use `redirect: 'manual'` per `.cursor/rules/rate-limit-awareness.mdc`:
     • `redirect: 'follow'` would download the entire homepage HTML
       (~50-200KB) through Cloudflare on every welcome-sticky CTA
       click. That's bytes through the WAF for nothing — the only
       thing we need from the redirect chain is the `Set-Cookie`
       header from the 302, which the browser honors regardless of
       whether `fetch` follows the redirect.
     • `'manual'` returns an opaqueredirect response immediately
       after the 302; the cookie is still set because cookie writes
       happen at the network layer before the redirect-handling
       decision in `fetch`. Verified against Shopify's storefront
       behavior: the `discount_code` cookie shows up in subsequent
       `/cart/*` requests under both modes.

     Treated as best-effort: a 404 (typo, expired code) or network
     blip resolves silently and the add-to-cart proceeds without the
     discount rather than failing the whole interaction. The user
     can still paste the code manually from the sticky's copy chip. */
  function applyDiscount(code) {
    if (!code) return Promise.resolve();
    return fetch('/discount/' + encodeURIComponent(code), {
      method: 'GET',
      credentials: 'same-origin',
      redirect: 'manual'
    }).then(function () { return true; })
      .catch(function (err) {
        try { console.warn('[dd-welcome-sticky] discount apply failed', err); } catch (_e) {}
        return false;
      });
  }

  function addToCart(sticky, cta, labelEl, labels, variantId) {
    cta.classList.add('is-loading');
    if (labelEl) labelEl.textContent = labels.loading;

    /* Mirror the section bundle dd-card2.js + dd-sticky-footer.js
       use so the drawer + bubble re-render in one round-trip. */
    var sections = 'cart-drawer,cart-icon-bubble';
    var code = sticky.getAttribute('data-dd-welcome-code') || '';

    /* Order matters: apply the discount cookie BEFORE the add so the
       /cart/add.js section HTML response already reflects the
       discounted price. If we applied after, the drawer's first paint
       would briefly show the un-discounted total before a follow-up
       cart fetch corrected it — a visible flicker that erodes trust
       in the offer. The serial latency cost (~80–150ms extra) is
       paid once per cart-create and is masked by the existing
       `is-loading` spinner state on the CTA. */
    applyDiscount(code).then(function () {
      var addPromise;
      if (window.DDCartApi && typeof window.DDCartApi.add === 'function') {
        addPromise = window.DDCartApi.add(Number(variantId), 1, sections);
      } else {
        /* Fallback. DDCartApi is loaded by the section's <script>
           tag, but defer ordering can lose against this module on
           rare bfcache restores; covering the gap keeps the CTA
           responsive even if the helper isn't ready yet. */
        addPromise = fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({
            items: [{ id: Number(variantId), quantity: 1 }],
            sections: sections
          })
        }).then(function (res) {
          if (!res.ok) throw new Error('cart/add failed: ' + res.status);
          return res.json();
        });
      }

      return addPromise;
    })
      .then(function (result) {
        cta.classList.remove('is-loading');
        /* Two state classes deliberately co-exist for the brief
           success window: `.is-added` is the transient flash that
           auto-removes after ~1.6s; `.is-in-cart` is the persistent
           "this variant is now in your cart" state that stays put
           until cart-update events flip it off. The CSS targets
           both with the same accent-surface treatment so the
           transition between them is visually invisible — only
           the auto-reset behavior differs. */
        cta.classList.add('is-added');
        cta.classList.add('is-in-cart');
        if (labelEl) labelEl.textContent = labels.inCart;

        /* Re-render the drawer with the freshly returned section
           HTML and open it. Same pattern dd-card2.js uses — the
           cart-drawer custom element exposes `renderContents` for
           drop-in HTML swap, falling back to `open()` if absent. */
        try {
          var drawer = document.querySelector('cart-drawer');
          if (drawer) {
            if (result && result.sections && typeof drawer.renderContents === 'function') {
              drawer.renderContents(result);
            } else if (typeof drawer.open === 'function') {
              drawer.open(cta);
            }
          }
        } catch (_e) { /* drawer open is best-effort */ }

        /* Notify other surfaces (mini-cart, header bubble, totals
           strips) so they refresh without a full page reload.
           The `cart-update` Dawn pub/sub event is the canonical
           channel — most theme modules subscribe to it. We also
           fire the legacy DOM `cart:updated` event for the few
           older listeners that haven't migrated yet. */
        try {
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'dd-welcome-sticky', result: result });
          }
          document.dispatchEvent(new CustomEvent('cart:updated', {
            bubbles: true,
            detail: { source: 'dd-welcome-sticky', result: result }
          }));
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        } catch (_e) {}

        /* Drop only `.is-added` after the flash window — `.is-in-cart`
           survives so the CTA stays in its persistent in-cart state. */
        window.setTimeout(function () {
          cta.classList.remove('is-added');
        }, ADDED_RESET_MS);
      })
      .catch(function (err) {
        try { console.warn('[dd-welcome-sticky] add-to-cart failed', err); } catch (_e) {}
        cta.classList.remove('is-loading');
        if (labelEl) labelEl.textContent = 'Try again';
        window.setTimeout(function () {
          if (labelEl) labelEl.textContent = labels.def;
        }, ADDED_RESET_MS);
      });
  }

  /* ------------------------------------------------------------------
     In-cart state sync. Server primes `.is-in-cart` on first paint
     via the Liquid `cart.items` loop. This listener keeps that state
     accurate after the page has loaded — handles all four directions
     of state change:

       1. Add via this sticky's CTA           (publish above)
       2. Add via mini-cart / cross-sell etc. (publish from those)
       3. Remove via mini-cart / cart drawer  (publish from those)
       4. bfcache restore (back/forward nav)  (`pageshow` below)

     Re-fetches `/cart.js` on every signal so we don't have to trust
     event payloads — Shopify's cart endpoint is fast and authoritative.
     ------------------------------------------------------------------ */

  function wireInCartSync(sticky) {
    /* Two CTAs (desktop + mobile) need to stay in lockstep so a
       resize across the 640px breakpoint never reveals a stale
       state. Iterate both on every cart-update signal. */
    var ctas = sticky.querySelectorAll('[data-dd-welcome-cta]');
    if (!ctas.length) return;
    var variantId = Number(sticky.getAttribute('data-dd-variant-id'));
    if (!variantId) return;

    /* Debounce coalesces the three near-simultaneous signals our own
       addToCart() fires (cartUpdate publish + cart:updated +
       cart:refresh), and any cascade other modules add on top. Without
       this, a single ATC tap produces /cart/add.js + 3× /cart.js — four
       Shopify requests for one user action, well over the ≤3 budget in
       rate-limit-awareness.mdc. The 100ms window is short enough that
       external add/remove events still feel instant but long enough to
       absorb any same-task event burst. */
    var _refreshTimer = 0;

    function scheduleRefresh() {
      if (_refreshTimer) window.clearTimeout(_refreshTimer);
      _refreshTimer = window.setTimeout(refresh, 100);
    }

    function refresh() {
      _refreshTimer = 0;
      /* Skip while ANY CTA is mid-flight: the optimistic state on
         that CTA is already correct, and a parallel /cart.js fetch
         could race the optimistic write. The other (idle) CTA gets
         updated on the next cart-update event after the inflight
         one settles, which is fine — both copies converge to the
         same authoritative state within ~1 round-trip. */
      for (var i = 0; i < ctas.length; i++) {
        if (ctas[i].classList.contains('is-loading')) return;
      }

      var fetchCart = (window.DDCartApi && typeof window.DDCartApi.getCart === 'function')
        ? window.DDCartApi.getCart()
        : fetch('/cart.js', { credentials: 'same-origin' }).then(function (r) { return r.json(); });

      fetchCart.then(function (cart) {
        var items = (cart && cart.items) || [];
        var present = false;
        for (var i = 0; i < items.length; i++) {
          if (Number(items[i].variant_id) === variantId) { present = true; break; }
        }
        for (var j = 0; j < ctas.length; j++) {
          var cta = ctas[j];
          var labelEl = cta.querySelector('[data-dd-welcome-cta-label]');
          applyInCartState(cta, labelEl, present);
        }
      }).catch(function () { /* network blip — ignore */ });
    }

    /* Dawn pub/sub channel — most modules use this. */
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, scheduleRefresh);
    }
    /* Legacy DOM event channel — covers older modules. */
    document.addEventListener('cart:updated', scheduleRefresh);
    document.addEventListener('cart:refresh', scheduleRefresh);

    /* bfcache: when the user nav-backs to the homepage, the cart
       may have changed in another tab; `pageshow.persisted` is the
       canonical signal that we're being restored from cache. */
    window.addEventListener('pageshow', function (e) {
      if (e.persisted) scheduleRefresh();
    });
  }

  function applyInCartState(cta, labelEl, present) {
    var labels = cta._ddLabels || {};
    if (present) {
      if (cta.classList.contains('is-in-cart')) return;
      cta.classList.add('is-in-cart');
      if (labelEl && labels.inCart) labelEl.textContent = labels.inCart;
      cta.setAttribute(
        'aria-label',
        (labels.inCart || 'Added') + ' — open cart'
      );
    } else {
      if (!cta.classList.contains('is-in-cart')) return;
      cta.classList.remove('is-in-cart');
      cta.classList.remove('is-added');
      if (labelEl && labels.def) labelEl.textContent = labels.def;
      cta.setAttribute('aria-label', labels.def || 'Add to cart');
    }
  }

  /* ------------------------------------------------------------------
     Scroll lifecycle
     ------------------------------------------------------------------ */

  /* ------------------------------------------------------------------
     Footer-in-view hide — once the page footer scrolls into the
     viewport (or is about to), slide the sticky out so it doesn't
     overlap the footer's CTAs / legal links. Symmetric: scrolling
     back up reveals it again. We don't unmount or remove .is-shown
     here — we only toggle the .is-footer-visible class and let CSS
     handle the transform/opacity reversal, so the user can scroll
     back up and the sticky slides right back in without any state
     reconstruction.

     Why this DOESN'T observe the footer element directly:
       The homepage uses a parallax-reveal pattern —
       `.dd-footer-section { position: fixed; bottom: 0; z-index: 0 }`
       pins the footer to the bottom of the viewport, with
       `#MainContent { z-index: 1 }` painting OVER it as the page
       scrolls. IntersectionObserver is purely geometry-based and
       doesn't read z-index, so the fixed footer's bbox always
       intersects the viewport, regardless of whether the user can
       actually see it. An IO observing the footer would fire
       isIntersecting:true the instant it's set up, hiding the
       sticky permanently. (Mobile/tablet override the footer to
       `position: static`, so an IO would behave correctly there —
       but we want one signal that's right on every layout.)

     The real signal: `#MainContent`'s bottom edge approaching the
     bottom of the viewport.
       - Desktop: that's literally the parallax reveal point — the
         moment MainContent's rounded bottom slides past viewport
         bottom is the moment the fixed footer becomes visible
         from underneath.
       - Mobile (footer in flow): same edge marks "footer is about
         to enter viewport" since the footer is the next sibling
         after MainContent in the document.
     One scroll-driven check on `#MainContent.getBoundingClientRect()`
     covers both layouts without per-template branching.
     ------------------------------------------------------------------ */

  function wireFooterHide(sticky) {
    var main = document.getElementById('MainContent');
    if (!main) return;

    var rafId = 0;
    var hidden = false;

    function evaluate() {
      rafId = 0;
      var rect = main.getBoundingClientRect();
      var vh = window.innerHeight || document.documentElement.clientHeight || 0;
      /* Anticipate the reveal by ~96px so the slide-out starts a moment
         BEFORE MainContent's bottom edge crosses viewport bottom — the
         sticky's own footprint (height + bottom inset) is roughly that
         range, so leaving early avoids the overlap window where the
         sticky visually competes with the footer's first-visible
         pixels. */
      var revealBuffer = 96;
      var atReveal = rect.bottom <= vh + revealBuffer;
      if (atReveal === hidden) return;
      hidden = atReveal;
      sticky.classList.toggle('is-footer-visible', atReveal);
      dbg('footer.toggle', { isFooterVisible: atReveal, mainBottom: Math.round(rect.bottom), vh: vh });
    }

    function onScroll() {
      if (rafId) return;
      rafId = window.requestAnimationFrame(evaluate);
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    /* Initial eval — covers deep-link scrolls (`?#anchor`) that already
       land near the bottom of the page. */
    window.requestAnimationFrame(evaluate);
  }

  function wireScroll(sticky) {
    var rafId = 0;
    var shown = false;
    /* Threshold is set per-section via the `scroll_threshold_vh`
       schema setting → forwarded as a data attribute. Stored as
       a fraction here so the per-tick math is just a multiply. */
    var thresholdFraction = parseFloat(sticky.getAttribute('data-dd-threshold-vh') || '60') / 100;
    if (!isFinite(thresholdFraction) || thresholdFraction < 0) thresholdFraction = 0.6;

    function evaluate() {
      rafId = 0;
      /* Recompute the threshold each tick — viewport height
         changes on mobile rotation and on iOS URL-bar collapse. */
      var threshold = (window.innerHeight || 600) * thresholdFraction;
      var scrollY = window.scrollY || window.pageYOffset || 0;
      var pastThreshold = scrollY >= threshold;
      if (pastThreshold === shown) return;
      shown = pastThreshold;
      if (shown) {
        /* CSS default is `display: none`; flipping .is-shown swaps
           it to `display: block` and runs the dd-nudge-slide-in
           keyframe in one step. No reflow trick or [hidden] dance
           needed — the keyframe fires from its `from` state at the
           moment the rule applies. */
        sticky.classList.add('is-shown');
        sticky.setAttribute('aria-hidden', 'false');
      } else {
        sticky.classList.remove('is-shown');
        sticky.setAttribute('aria-hidden', 'true');
      }
      dbg('scroll.toggle', {
        shown: shown,
        scrollY: Math.round(scrollY),
        threshold: Math.round(threshold),
        isFooterVisible: sticky.classList.contains('is-footer-visible')
      });
    }

    function onScroll() {
      if (rafId) return;
      rafId = window.requestAnimationFrame(evaluate);
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    /* First eval covers deep-link scrolls (`?#anchor`) that land
       below the threshold — the sticky should reveal on first
       paint without an extra interaction. */
    window.requestAnimationFrame(evaluate);
  }

  /* ------------------------------------------------------------------
     Countdown — second-tick HRS:MIN:SEC. Battery-aware: pauses
     when the tab is hidden, resumes (with a snap-render) on focus.
     ------------------------------------------------------------------ */

  function wireCountdown(sticky, offer) {
    /* No countdown column rendered (schema toggle off) — bail. */
    if (!sticky.querySelector('[data-dd-welcome-countdown]')) {
      /* Even without the visible cells we still need expiry
         enforcement so the sticky drops at the right moment. */
      runExpiryWatchdog(sticky, offer);
      return;
    }

    var timer = 0;

    function tick() {
      if (!isFresh(offer.endsAt)) {
        stop();
        slideOut(sticky);
        return;
      }
      renderCountdown(sticky, offer.endsAt);
    }

    function start() {
      if (timer) return;
      tick();
      timer = window.setInterval(tick, COUNTDOWN_TICK_MS);
    }

    function stop() {
      if (!timer) return;
      window.clearInterval(timer);
      timer = 0;
    }

    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'visible') start();
      else stop();
    });

    /* Pause when the sticky itself is offscreen (scrolled to top
       and not yet revealed) — saves a tick/sec on a hot path. */
    var io;
    if ('IntersectionObserver' in window) {
      io = new IntersectionObserver(function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (entries[i].isIntersecting) start();
          else if (!sticky.classList.contains('is-shown')) stop();
        }
      });
      io.observe(sticky);
    }

    start();
  }

  function runExpiryWatchdog(sticky, offer) {
    /* Lightweight 60s check used when the visible countdown is
       turned off in the schema — we still need to enforce expiry
       client-side so a tab open across the boundary doesn't keep
       a stale offer visible. */
    var t = window.setInterval(function () {
      if (!isFresh(offer.endsAt)) {
        window.clearInterval(t);
        slideOut(sticky);
      }
    }, 60000);
  }

  function renderCountdown(sticky, endsAt) {
    var msLeft = new Date(endsAt).getTime() - Date.now();
    if (!isFinite(msLeft) || msLeft <= 0) {
      writeCells(sticky, 0, 0, 0);
      return;
    }
    var totalSeconds = Math.floor(msLeft / 1000);
    var hours = Math.floor(totalSeconds / 3600);
    var minutes = Math.floor((totalSeconds % 3600) / 60);
    var seconds = totalSeconds % 60;
    writeCells(sticky, hours, minutes, seconds);
  }

  function writeCells(sticky, h, m, s) {
    /* Update both desktop + mobile cell trios. NodeList iteration
       handles 3 cells (single variant) and 6 cells (both variants)
       with the same code path. */
    writeAll(sticky, '[data-dd-cd-hours]', pad2(h));
    writeAll(sticky, '[data-dd-cd-minutes]', pad2(m));
    writeAll(sticky, '[data-dd-cd-seconds]', pad2(s));
  }

  function writeAll(root, selector, text) {
    var els = root.querySelectorAll(selector);
    for (var i = 0; i < els.length; i++) els[i].textContent = text;
  }

  function pad2(n) {
    n = Math.max(0, Number(n) || 0);
    /* Hours can exceed 99 in theory (long offer windows). Cap
       the visible string at 3 digits so a 100h+ offer doesn't
       overflow the cell. */
    if (n >= 100) return String(n);
    return n < 10 ? '0' + n : String(n);
  }
})();
